import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Loader2, Table2, CalendarCheck, ClipboardList, LineChart as LineIcon, ListChecks, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { GradeTrendChart } from "@/components/GradeTrendChart";

function useExport() {
  const [busy, setBusy] = useState("");
  const download = async (key, path, params, format, filename) => {
    setBusy(`${key}-${format}`);
    try {
      const res = await api.get(path, { params: { ...params, format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}.${format === "pdf" ? "pdf" : "xlsx"}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Laporan diunduh");
    } catch (e) {
      toast.error("Gagal mengunduh laporan");
    } finally { setBusy(""); }
  };
  return { busy, download };
}

const KELAS_OPTS = ["all", "1", "2", "3", "4", "5", "6"];

function AccessCountdown({ info }) {
  const d = info.days_left;
  const expired = d < 0;
  const tone = expired ? "bg-rose-50 text-rose-700 border-rose-200"
    : d <= 3 ? "bg-rose-50 text-rose-700 border-rose-200"
    : d <= 14 ? "bg-amber-50 text-amber-700 border-amber-200"
    : "bg-emerald-50 text-emerald-700 border-emerald-200";
  const label = expired ? "Masa akses berakhir" : d === 0 ? "Berakhir hari ini" : `${d} hari lagi`;
  return (
    <div className={`shrink-0 rounded-2xl border px-4 py-2.5 text-right ${tone}`} data-testid="access-countdown" title={`Masa akses berakhir ${info.access_end}`}>
      <div className="flex items-center justify-end gap-1.5 font-display font-bold text-lg leading-none">
        <Clock className="w-4 h-4" /> {label}
      </div>
      <p className="text-[11px] opacity-80 mt-1">Masa akses s/d {info.access_end}{info.source === "sekolah" ? " (sekolah)" : ""}</p>
    </div>
  );
}

function DateRange({ from, to, setFrom, setTo, prefix }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label className="text-xs text-slate-500">Dari Tanggal</Label>
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 rounded-xl" data-testid={`${prefix}-from`} />
      </div>
      <div>
        <Label className="text-xs text-slate-500">Sampai Tanggal</Label>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 rounded-xl" data-testid={`${prefix}-to`} />
      </div>
    </div>
  );
}

function KelasSelect({ value, onChange, testid, locked }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">Kelas</Label>
      <Select value={value} onValueChange={onChange} disabled={locked}>
        <SelectTrigger className="mt-1 rounded-xl" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Semua Kelas</SelectItem>
          {[1, 2, 3, 4, 5, 6].map((k) => <SelectItem key={k} value={String(k)}>Kelas {k}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function RombelSelect({ value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-xs text-slate-500">Rombel</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1 rounded-xl" data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Semua Rombel</SelectItem>
          {options.map((r) => <SelectItem key={r} value={r}>Rombel {r}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function StudentTrend({ students }) {
  const [sid, setSid] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [kelas, setKelas] = useState("all");

  const shown = kelas === "all" ? students : students.filter((s) => String(s.kelas) === kelas);

  useEffect(() => {
    if (!sid) { setData(null); return; }
    setLoading(true);
    api.get(`/students/${sid}/trend`).then((r) => setData(r.data)).catch(() => toast.error("Gagal memuat tren")).finally(() => setLoading(false));
  }, [sid]);

  return (
    <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="trend-section">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-700 flex items-center justify-center"><LineIcon className="w-5 h-5" /></div>
        <div>
          <h2 className="font-display text-lg font-semibold text-slate-800">Grafik Tren Nilai Murid</h2>
          <p className="text-sm text-slate-500">Pilih murid untuk melihat perkembangan nilai final dari waktu ke waktu.</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mb-5">
        <div>
          <Label className="text-xs text-slate-500">Saring Kelas</Label>
          <Select value={kelas} onValueChange={(v) => { setKelas(v); setSid(""); }}>
            <SelectTrigger className="mt-1 rounded-xl" data-testid="trend-kelas-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Kelas</SelectItem>
              {[1, 2, 3, 4, 5, 6].map((k) => <SelectItem key={k} value={String(k)}>Kelas {k}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs text-slate-500">Murid</Label>
          <Select value={sid} onValueChange={setSid}>
            <SelectTrigger className="mt-1 rounded-xl" data-testid="trend-student-select"><SelectValue placeholder="Pilih murid..." /></SelectTrigger>
            <SelectContent className="max-h-72">
              {shown.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`trend-student-opt-${s.id}`}>{s.name} · Kelas {s.kelas}{s.rombel || ""}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
      ) : !sid ? (
        <p className="text-sm text-slate-400 py-10 text-center" data-testid="trend-placeholder">Pilih murid terlebih dahulu.</p>
      ) : (
        <GradeTrendChart data={data} testId="guru-trend" />
      )}
    </section>
  );
}

function ExportButtons({ onExcel, onPdf, busy, keyName }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <Button variant="outline" className="rounded-xl text-emerald-700 border-emerald-200" onClick={onExcel} disabled={busy === `${keyName}-xlsx`} data-testid={`export-excel-${keyName}-btn`}>
        {busy === `${keyName}-xlsx` ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />} Excel
      </Button>
      <Button variant="outline" className="rounded-xl text-rose-700 border-rose-200" onClick={onPdf} disabled={busy === `${keyName}-pdf`} data-testid={`export-pdf-${keyName}-btn`}>
        {busy === `${keyName}-pdf` ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />} PDF
      </Button>
    </div>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const wali = user?.teacher_type === "wali" && user?.wali_kelas;
  const { busy, download } = useExport();
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);

  // leger filters
  const [gKelas, setGKelas] = useState(wali ? String(user.wali_kelas) : "3");
  const [gRombel, setGRombel] = useState("all");
  const [gSubject, setGSubject] = useState("all");
  const [gFrom, setGFrom] = useState("");
  const [gTo, setGTo] = useState("");
  const [leger, setLeger] = useState(null);
  const [loadingLeger, setLoadingLeger] = useState(false);

  // laporan per mapel (nilai per tugas)
  const [pSubject, setPSubject] = useState("");
  const [pKelas, setPKelas] = useState(wali ? String(user.wali_kelas) : "all");
  const [pRombel, setPRombel] = useState("all");
  const [pFrom, setPFrom] = useState("");
  const [pTo, setPTo] = useState("");
  const [perMapel, setPerMapel] = useState(null);
  const [loadingPer, setLoadingPer] = useState(false);
  const [accessInfo, setAccessInfo] = useState(null);

  // attendance filters
  const [aKelas, setAKelas] = useState(wali ? String(user.wali_kelas) : "all");
  const [aFrom, setAFrom] = useState("");
  const [aTo, setATo] = useState("");

  // assignment filters
  const [tKelas, setTKelas] = useState(wali ? String(user.wali_kelas) : "all");
  const [tFrom, setTFrom] = useState("");
  const [tTo, setTTo] = useState("");

  useEffect(() => {
    api.get("/subjects").then((r) => { setSubjects(r.data); if (r.data[0]) setPSubject((cur) => cur || r.data[0].id); });
    api.get("/students").then((r) => setStudents(r.data));
    api.get("/me/access-info").then((r) => setAccessInfo(r.data)).catch(() => setAccessInfo(null));
  }, []);

  const rombelOpts = [...new Set(students.filter((s) => gKelas === "all" || String(s.kelas) === gKelas).map((s) => s.rombel).filter(Boolean))].sort();

  const gParams = () => ({
    kelas: gKelas === "all" ? undefined : gKelas,
    rombel: gRombel === "all" ? undefined : gRombel,
    subject_id: gSubject === "all" ? undefined : gSubject,
    date_from: gFrom || undefined, date_to: gTo || undefined,
  });

  const loadLeger = () => {
    setLoadingLeger(true);
    api.get("/reports/grades-leger", { params: gParams() }).then((r) => setLeger(r.data)).finally(() => setLoadingLeger(false));
  };
  useEffect(() => { loadLeger(); }, [gKelas, gRombel, gSubject, gFrom, gTo]);

  const pRombelOpts = [...new Set(students.filter((s) => pKelas === "all" || String(s.kelas) === pKelas).map((s) => s.rombel).filter(Boolean))].sort();
  const pParams = () => ({
    subject_id: pSubject || undefined,
    kelas: pKelas === "all" ? undefined : pKelas,
    rombel: pRombel === "all" ? undefined : pRombel,
    date_from: pFrom || undefined, date_to: pTo || undefined,
  });
  useEffect(() => {
    if (!pSubject) { setPerMapel(null); return; }
    setLoadingPer(true);
    api.get("/reports/subject-tasks", { params: { ...pParams(), format: "json" } })
      .then((r) => setPerMapel(r.data)).catch(() => setPerMapel(null)).finally(() => setLoadingPer(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pSubject, pKelas, pRombel, pFrom, pTo]);

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-800">Laporan</h1>
          <p className="text-slate-500 mt-1">
            {wali ? `Laporan khusus kelas ${user.wali_kelas}${user.wali_rombel || ""} yang kamu pegang. ` : "Leger nilai per mapel & laporan absensi/tugas. "}
            Semua bisa difilter dan diekspor ke Excel atau PDF.
          </p>
        </div>
        {accessInfo && accessInfo.days_left !== null && <AccessCountdown info={accessInfo} />}
      </div>

      <StudentTrend students={students} />

      {/* LEGER NILAI */}
      <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-700 flex items-center justify-center"><Table2 className="w-5 h-5" /></div>
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-800">Leger Nilai (Rapor)</h2>
            <p className="text-sm text-slate-500">Baris = murid, kolom = mata pelajaran. Nilai = rata-rata nilai final.</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
          <KelasSelect value={gKelas} onChange={(v) => { setGKelas(v); setGRombel("all"); }} testid="leger-kelas-select" locked={!!wali} />
          {!(wali && user.wali_rombel) && <RombelSelect value={gRombel} onChange={setGRombel} options={rombelOpts} testid="leger-rombel-select" />}
          <div>
            <Label className="text-xs text-slate-500">Mata Pelajaran</Label>
            <Select value={gSubject} onValueChange={setGSubject}>
              <SelectTrigger className="mt-1 rounded-xl" data-testid="leger-subject-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Mapel</SelectItem>
                {subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><DateRange from={gFrom} to={gTo} setFrom={setGFrom} setTo={setGTo} prefix="leger" /></div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-200">
          {loadingLeger ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
          ) : !leger || leger.rows.length === 0 ? (
            <p className="text-sm text-slate-400 p-6 text-center">Tidak ada data untuk filter ini.</p>
          ) : (
            <table className="w-full text-sm" data-testid="leger-table">
              <thead>
                <tr className="bg-teal-600 text-white">
                  <th className="px-3 py-2.5 text-left font-semibold w-12">No</th>
                  <th className="px-3 py-2.5 text-left font-semibold min-w-[160px]">Nama Siswa</th>
                  <th className="px-3 py-2.5 text-center font-semibold">Kelas</th>
                  {leger.subjects.map((s) => <th key={s.id} className="px-3 py-2.5 text-center font-semibold whitespace-nowrap">{s.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {leger.rows.map((r, i) => (
                  <tr key={i} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                    <td className="px-3 py-2 text-slate-500">{r.No}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">{r["Nama Siswa"]}</td>
                    <td className="px-3 py-2 text-center text-slate-600">{r.Kelas}</td>
                    {leger.subjects.map((s) => {
                      const v = r[s.name];
                      return <td key={s.id} className={`px-3 py-2 text-center ${v === "-" ? "text-slate-300" : v < 75 ? "text-orange-600 font-semibold" : "text-slate-700 font-medium"}`}>{v}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-4 max-w-xs ml-auto">
          <ExportButtons keyName="grades" busy={busy}
            onExcel={() => download("grades", "/reports/grades", gParams(), "xlsx", "Leger_Nilai")}
            onPdf={() => download("grades", "/reports/grades", gParams(), "pdf", "Leger_Nilai")} />
        </div>
      </section>

      {/* LAPORAN PER MATA PELAJARAN (NILAI PER TUGAS) */}
      <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm" data-testid="subject-tasks-section">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center"><ListChecks className="w-5 h-5" /></div>
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-800">Laporan Mata Pelajaran (Nilai per Tugas)</h2>
            <p className="text-sm text-slate-500">Baris = murid, kolom = tiap tugas (judul kolom = tanggal pembuatan tugas).</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
          <div>
            <Label className="text-xs text-slate-500">Mata Pelajaran</Label>
            <Select value={pSubject} onValueChange={(id) => {
              setPSubject(id);
              if (!wali) {
                const subj = subjects.find((s) => s.id === id);
                const j = subj?.jenjang || [];
                setPKelas(j.length === 1 ? String(j[0]) : "all");
                setPRombel("all");
              }
            }}>
              <SelectTrigger className="mt-1 rounded-xl" data-testid="pmapel-subject-select"><SelectValue placeholder="Pilih mapel..." /></SelectTrigger>
              <SelectContent>
                {subjects.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <KelasSelect value={pKelas} onChange={(v) => { setPKelas(v); setPRombel("all"); }} testid="pmapel-kelas-select" locked={!!wali} />
          {!(wali && user.wali_rombel) && <RombelSelect value={pRombel} onChange={setPRombel} options={pRombelOpts} testid="pmapel-rombel-select" />}
          <div className="sm:col-span-2"><DateRange from={pFrom} to={pTo} setFrom={setPFrom} setTo={setPTo} prefix="pmapel" /></div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-200">
          {loadingPer ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
          ) : !pSubject ? (
            <p className="text-sm text-slate-400 p-6 text-center">Pilih mata pelajaran terlebih dahulu.</p>
          ) : !perMapel || perMapel.tasks.length === 0 ? (
            <p className="text-sm text-slate-400 p-6 text-center" data-testid="pmapel-empty">Belum ada tugas untuk mapel/filter ini.</p>
          ) : (
            <table className="w-full text-sm" data-testid="pmapel-table">
              <thead>
                <tr className="bg-emerald-600 text-white">
                  <th className="px-3 py-2 text-left font-semibold w-12">No</th>
                  <th className="px-3 py-2 text-left font-semibold min-w-[160px]">Nama Siswa</th>
                  {perMapel.tasks.map((t) => (
                    <th key={t.id} className="px-3 py-2 text-center font-semibold whitespace-nowrap" title={t.title}>{t.date}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {perMapel.rows.map((r, i) => (
                  <tr key={i} className={i % 2 ? "bg-slate-50" : "bg-white"}>
                    <td className="px-3 py-2 text-slate-500">{r.no}</td>
                    <td className="px-3 py-2 font-medium text-slate-800">{r.name}</td>
                    {r.scores.map((v, j) => (
                      <td key={j} className={`px-3 py-2 text-center ${v === "-" ? "text-slate-300" : v < 75 ? "text-orange-600 font-semibold" : "text-slate-700 font-medium"}`}>{v}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="mt-4 max-w-xs ml-auto">
          <ExportButtons keyName="subjtasks" busy={busy}
            onExcel={() => pSubject && download("subjtasks", "/reports/subject-tasks", pParams(), "xlsx", `Nilai_${(perMapel?.subject || "Mapel").replace(/ /g, "_")}`)}
            onPdf={() => pSubject && download("subjtasks", "/reports/subject-tasks", pParams(), "pdf", `Nilai_${(perMapel?.subject || "Mapel").replace(/ /g, "_")}`)} />
        </div>
      </section>

      {/* ABSENSI & TUGAS */}
      <div className="grid md:grid-cols-2 gap-6">
        <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-teal-50 text-teal-700 flex items-center justify-center"><CalendarCheck className="w-5 h-5" /></div>
            <h2 className="font-display text-lg font-semibold text-slate-800">Laporan Absensi</h2>
          </div>
          <div className="space-y-3">
            <KelasSelect value={aKelas} onChange={setAKelas} testid="att-kelas-select" locked={!!wali} />
            <DateRange from={aFrom} to={aTo} setFrom={setAFrom} setTo={setATo} prefix="att" />
          </div>
          <div className="mt-4">
            <ExportButtons keyName="attendance" busy={busy}
              onExcel={() => download("attendance", "/reports/attendance", { kelas: aKelas === "all" ? undefined : aKelas, date_from: aFrom || undefined, date_to: aTo || undefined }, "xlsx", "Laporan_Absensi")}
              onPdf={() => download("attendance", "/reports/attendance", { kelas: aKelas === "all" ? undefined : aKelas, date_from: aFrom || undefined, date_to: aTo || undefined }, "pdf", "Laporan_Absensi")} />
          </div>
        </section>

        <section className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center"><ClipboardList className="w-5 h-5" /></div>
            <h2 className="font-display text-lg font-semibold text-slate-800">Laporan Tugas</h2>
          </div>
          <div className="space-y-3">
            <KelasSelect value={tKelas} onChange={setTKelas} testid="tugas-kelas-select" locked={!!wali} />
            <DateRange from={tFrom} to={tTo} setFrom={setTFrom} setTo={setTTo} prefix="tugas" />
          </div>
          <div className="mt-4">
            <ExportButtons keyName="assignments" busy={busy}
              onExcel={() => download("assignments", "/reports/assignments", { kelas: tKelas === "all" ? undefined : tKelas, date_from: tFrom || undefined, date_to: tTo || undefined }, "xlsx", "Laporan_Tugas")}
              onPdf={() => download("assignments", "/reports/assignments", { kelas: tKelas === "all" ? undefined : tKelas, date_from: tFrom || undefined, date_to: tTo || undefined }, "pdf", "Laporan_Tugas")} />
          </div>
        </section>
      </div>
    </div>
  );
}
